import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PdMark from '../components/PdMark';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const formatCode = (raw) => {
  const cleaned = (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 24);
  return cleaned.replace(/(.{4})(?=.)/g, '$1-');
};

/**
 * Forgot-password page. The recovery code (issued at registration or from
 * Profile → Security) unseals the private key so a new password can be set
 * without losing any documents.
 */
export const RecoverAccount = () => {
  const navigate = useNavigate();
  const { recoverAccount } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [loading, setLoading] = useState(false);

  const passwordOk = newPassword.length >= 8 && /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) && /\d/.test(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordOk) {
      toast.error('Your new password needs 8 or more characters, with a capital letter, a lower-case letter and a number');
      return;
    }
    if (newPassword !== confirm) {
      toast.error('The two passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await recoverAccount({
        email: email.trim(),
        recoveryCode: code,
        newPassword,
        mfaCode: mfaCode.trim(),
      });
      // Signed in now: the route guard moves us on, and the app root shows
      // the new recovery code dialog (see AuthContext) until it's confirmed.
      toast.success('Password changed. Save your new recovery code — the old one no longer works.');
      navigate('/dashboard');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 423) {
        toast.error(err.response.data.detail);
      } else {
        toast.error(err?.response?.data?.detail || 'We could not recover the account with those details');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ground flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-3 rounded-md" aria-label="Precious Data home">
            <PdMark size={28} className="text-brand" />
            <span className="font-heading text-[18px] font-bold tracking-[-0.02em] text-ink">Precious Data</span>
          </Link>
          <Link to="/login" className="text-[15px] text-ink-70 hover:text-ink transition-colors duration-state">
            Back to sign in
          </Link>
        </div>

        <div className="bg-surface rounded-xl p-7 sm:p-9">
          <h1 className="font-heading text-[28px] font-bold tracking-[-0.035em] leading-[1.05]">Recover your account</h1>
          <p className="text-ink-70 mt-3">
            Use the recovery code you saved when you signed up to set a new password. Your documents stay exactly as they were.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5 mt-7">
            <div className="space-y-2">
              <Label htmlFor="recover-email" className="text-[15px] font-semibold block">What&apos;s your email address?</Label>
              <Input id="recover-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="you@example.com" required
                     autoComplete="username" data-testid="recover-email-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recover-code" className="text-[15px] font-semibold block">What&apos;s your recovery code?</Label>
              <p className="text-sm text-ink-55">Dashes and capitals don&apos;t matter — type it as you see it.</p>
              <Input id="recover-code" value={code} onChange={(e) => setCode(formatCode(e.target.value))}
                     placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX" className="font-mono tracking-wider"
                     required autoComplete="off" spellCheck={false} data-testid="recover-code-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recover-password" className="text-[15px] font-semibold block">Choose a new password</Label>
              <p className="text-sm text-ink-55">8 or more characters, with a capital letter, a lower-case letter and a number.</p>
              <Input id="recover-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                     required autoComplete="new-password" data-testid="recover-password-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recover-confirm" className="text-[15px] font-semibold block">Type it once more</Label>
              <Input id="recover-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                     required autoComplete="new-password" data-testid="recover-confirm-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recover-mfa" className="text-[15px] font-semibold block">Authenticator code</Label>
              <p className="text-sm text-ink-55">Only if you set up two-factor authentication. Leave it blank otherwise.</p>
              <Input id="recover-mfa" inputMode="numeric" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
                     placeholder="123456" autoComplete="one-time-code"
                     data-testid="recover-mfa-input" />
            </div>

            <Button type="submit" disabled={loading} className="w-full" data-testid="recover-submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Set the new password and sign in'}
            </Button>
          </form>

          <p className="text-sm text-ink-55 mt-6">
            No recovery code? Without it, or your password, nobody can unlock your encrypted records,
            including us. Five wrong attempts lock the account until an administrator unlocks it.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RecoverAccount;
